/**
 * Toddler Daily Report Tab Component
 * Daily tracking for toddlers: meals, diaper/toilet, sleep, mood, activities, milestones
 * With save to database, history view, and delete functionality
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Baby, Printer, Plus, Trash2, Moon, Utensils, BookOpen, Save, History, ChevronLeft, CheckSquare, Square, Mail, Download } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { exportDailyReportPDF } from "@/utils/pdfExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ToddlerDailyReportTab = ({ children, centerName = "Childcare Center" }) => {
  const [view, setView] = useState("form");
  const [savedReports, setSavedReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const [selectedChild, setSelectedChild] = useState("");
  const [reportDate, setReportDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffName, setStaffName] = useState("");
  
  // Mood checkboxes
  const [mood, setMood] = useState({
    happy: false,
    fussy: false,
    curious: false,
    irritable: false,
    sleepy: false,
    sick: false
  });

  // Today I learned
  const [todayLearned, setTodayLearned] = useState("");
  
  // Meals entries - 4 blank rows for print
  const [meals, setMeals] = useState([
    { id: 1, time: "", type: "", amount: "" },
    { id: 2, time: "", type: "", amount: "" },
    { id: 3, time: "", type: "", amount: "" },
    { id: 4, time: "", type: "", amount: "" }
  ]);
  
  // Diaper/Toilet changes - 4 blank rows with write-in D/T
  const [bathroomEntries, setBathroomEntries] = useState([
    { id: 1, time: "", dtType: "", dry: false, wet: false, bm: false },
    { id: 2, time: "", dtType: "", dry: false, wet: false, bm: false },
    { id: 3, time: "", dtType: "", dry: false, wet: false, bm: false },
    { id: 4, time: "", dtType: "", dry: false, wet: false, bm: false }
  ]);
  
  // Sleep periods - 4 blank rows
  const [sleepPeriods, setSleepPeriods] = useState([
    { id: 1, start: "", end: "" },
    { id: 2, start: "", end: "" },
    { id: 3, start: "", end: "" },
    { id: 4, start: "", end: "" }
  ]);
  
  // Please Bring More items
  const [bringMore, setBringMore] = useState({
    diapers: false,
    wipes: false,
    ointment: false,
    clothes: false,
    blanket: false
  });
  
  // Notes
  const [activities, setActivities] = useState("");
  const [comments, setComments] = useState("");

  // Filter to show only Full Time and Part Time children
  const displayChildren = children.filter(c => {
    if (c.active === false) return false;
    const category = c.category?.toLowerCase().replace(/[\s_-]/g, '') || '';
    return category === 'fulltime' || category === 'parttime' || 
           category === 'full_time' || category === 'part_time' ||
           category === 'full-time' || category === 'part-time';
  });

  const getChildName = (childId = selectedChild) => {
    const child = children.find(c => c.id === childId);
    return child ? child.name : "";
  };

  // Fetch saved reports
  const fetchSavedReports = async () => {
    setLoadingReports(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/daily-reports?report_type=toddler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedReports(response.data || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Fetch reports on mount
  useEffect(() => {
    fetchSavedReports();
  }, []);

  useEffect(() => {
    if (view === "history") {
      fetchSavedReports();
    }
  }, [view]);

  // Get parent email for child from pickup persons
  const getChildEmail = (childId = selectedChild) => {
    const child = children.find(c => c.id === childId);
    if (!child) return "";
    // Get email from first pickup person (parent/guardian)
    const pickupPersons = child.pickupPersons || [];
    if (pickupPersons.length > 0 && pickupPersons[0].email) {
      return pickupPersons[0].email;
    }
    return "";
  };

  // Save report to database
  const handleSave = async () => {
    if (!selectedChild || !staffName) {
      toast.error("Please select a child and enter staff name");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const reportData = {
        childId: selectedChild,
        childName: getChildName(),
        reportDate,
        staffName,
        reportType: "toddler",
        mood,
        todayLearned,
        meals,
        diaperChanges: bathroomEntries,
        sleepPeriods,
        bringMore,
        activities,
        comments,
        parentEmail: getChildEmail()
      };

      await axios.post(`${API}/daily-reports`, reportData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Report saved successfully!");
      fetchSavedReports();
      clearForm();
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  // Delete single report
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/daily-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report deleted");
      fetchSavedReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  // Bulk delete reports
  const handleBulkDelete = async () => {
    if (selectedReports.length === 0) {
      toast.error("No reports selected");
      return;
    }
    
    if (!window.confirm(`Delete ${selectedReports.length} selected reports?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/daily-reports/bulk-delete`, 
        { ids: selectedReports },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${selectedReports.length} reports`);
      setSelectedReports([]);
      fetchSavedReports();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete reports");
    }
  };

  // Toggle report selection
  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedReports.length === savedReports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(savedReports.map(r => r.id));
    }
  };

  // Load report into form
  const loadReport = (report) => {
    setSelectedChild(report.childId);
    setReportDate(report.reportDate);
    setStaffName(report.staffName);
    setMood(report.mood || {});
    setTodayLearned(report.todayLearned || "");
    setMeals(report.meals || [{ id: 1, time: "", type: "", amount: "" }]);
    setBathroomEntries(report.diaperChanges || [{ id: 1, time: "", dtType: "", dry: false, wet: false, bm: false }]);
    setSleepPeriods(report.sleepPeriods || [{ id: 1, start: "", end: "" }]);
    setBringMore(report.bringMore || {});
    setActivities(report.activities || "");
    setComments(report.comments || "");
    setView("form");
  };

  // Email report to parent - with manual email option
  const handleEmailReport = async (report) => {
    // Try to get email from: saved report > child's pickup person
    let parentEmail = report.parentEmail;
    
    if (!parentEmail) {
      const child = children.find(c => c.id === report.childId);
      const pickupPersons = child?.pickupPersons || [];
      if (pickupPersons.length > 0 && pickupPersons[0].email) {
        parentEmail = pickupPersons[0].email;
      }
    }
    
    if (!parentEmail) {
      // Prompt for email if none found
      const inputEmail = window.prompt("No parent email on file. Enter email address to send report:");
      if (!inputEmail || !inputEmail.includes('@')) {
        if (inputEmail) toast.error("Please enter a valid email address");
        return;
      }
      parentEmail = inputEmail;
    } else {
      if (!window.confirm(`Send report to ${parentEmail}?`)) return;
    }
    
    setEmailing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/daily-reports/email`, {
        reportId: report.id,
        parentEmail: parentEmail,
        childName: report.childName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Report emailed to ${parentEmail}`);
    } catch (error) {
      console.error("Error emailing report:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailing(false);
    }
  };

  const addMeal = () => {
    setMeals([...meals, { id: Date.now(), time: "", type: "", amount: "" }]);
  };

  const updateMeal = (id, field, value) => {
    setMeals(meals.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const removeMeal = (id) => {
    if (meals.length > 1) {
      setMeals(meals.filter(m => m.id !== id));
    }
  };

  const addBathroomEntry = () => {
    setBathroomEntries([...bathroomEntries, { id: Date.now(), time: "", isDiaper: true, dry: false, wet: false, bm: false }]);
  };

  const updateBathroomEntry = (id, field, value) => {
    setBathroomEntries(bathroomEntries.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const removeBathroomEntry = (id) => {
    if (bathroomEntries.length > 1) {
      setBathroomEntries(bathroomEntries.filter(b => b.id !== id));
    }
  };

  const updateSleepPeriod = (id, field, value) => {
    setSleepPeriods(sleepPeriods.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handlePrint = () => {
    window.print();
  };

  const clearForm = () => {
    setSelectedChild("");
    setStaffName("");
    setMood({ happy: false, fussy: false, curious: false, irritable: false, sleepy: false, sick: false });
    setTodayLearned("");
    setMeals([
      { id: 1, time: "", type: "", amount: "" },
      { id: 2, time: "", type: "", amount: "" },
      { id: 3, time: "", type: "", amount: "" },
      { id: 4, time: "", type: "", amount: "" }
    ]);
    setBathroomEntries([
      { id: 1, time: "", dtType: "", dry: false, wet: false, bm: false },
      { id: 2, time: "", dtType: "", dry: false, wet: false, bm: false },
      { id: 3, time: "", dtType: "", dry: false, wet: false, bm: false },
      { id: 4, time: "", dtType: "", dry: false, wet: false, bm: false }
    ]);
    setSleepPeriods([
      { id: 1, start: "", end: "" },
      { id: 2, start: "", end: "" },
      { id: 3, start: "", end: "" },
      { id: 4, start: "", end: "" }
    ]);
    setBringMore({ diapers: false, wipes: false, ointment: false, clothes: false, blanket: false });
    setActivities("");
    setComments("");
  };

  // History View
  if (view === "history") {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-purple-200" data-testid="toddler-daily-report-history">
        <CardHeader className="bg-gradient-to-r from-purple-100 to-pink-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl text-purple-800 flex items-center gap-3">
              <History className="w-7 h-7" />
              Saved Toddler Reports
            </CardTitle>
            <Button 
              onClick={() => setView("form")} 
              variant="outline" 
              className="border-purple-300 text-purple-600"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Form
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Bulk Actions */}
          <div className="flex items-center justify-between mb-4 p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Button
                onClick={toggleSelectAll}
                variant="outline"
                size="sm"
                className="border-purple-300"
                data-testid="select-all-toddler-reports"
              >
                {selectedReports.length === savedReports.length && savedReports.length > 0 ? (
                  <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</>
                ) : (
                  <><Square className="w-4 h-4 mr-1" /> Select All</>
                )}
              </Button>
              <span className="text-sm text-gray-600">
                {selectedReports.length} of {savedReports.length} selected
              </span>
            </div>
            {selectedReports.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                variant="destructive"
                size="sm"
                data-testid="bulk-delete-toddler-reports"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected ({selectedReports.length})
              </Button>
            )}
          </div>

          {/* Reports List */}
          {loadingReports ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading reports...</p>
            </div>
          ) : savedReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Baby className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No saved toddler reports yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedReports.map((report) => (
                <div 
                  key={report.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                    selectedReports.includes(report.id) 
                      ? 'border-purple-400 bg-purple-50' 
                      : 'border-gray-200 hover:border-purple-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedReports.includes(report.id)}
                      onCheckedChange={() => toggleReportSelection(report.id)}
                      className="border-purple-400 data-[state=checked]:bg-purple-500"
                    />
                    <div>
                      <p className="font-semibold text-purple-800">{report.childName}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(report.reportDate + 'T12:00:00'), 'MMM d, yyyy')} • Staff: {report.staffName}
                      </p>
                      {report.todayLearned && (
                        <p className="text-xs text-purple-600 mt-1">
                          Learned: {report.todayLearned.substring(0, 50)}{report.todayLearned.length > 50 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleEmailReport(report)}
                      disabled={emailing}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-600"
                      title="Email to parent"
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => loadReport(report)}
                      variant="outline"
                      size="sm"
                      className="border-purple-300 text-purple-600"
                    >
                      View
                    </Button>
                    <Button
                      onClick={() => handleDeleteReport(report.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Form View
  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-purple-200" data-testid="toddler-daily-report-tab">
      <CardHeader className="print:hidden bg-gradient-to-r from-purple-100 to-pink-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl text-purple-800 flex items-center gap-3">
              <Baby className="w-7 h-7" />
              Toddler Daily Report
            </CardTitle>
            <p className="text-purple-600 text-sm mt-1">Daily tracking for toddlers with toilet training support</p>
          </div>
          <Button 
            onClick={() => setView("history")} 
            variant="outline" 
            className="border-purple-300 text-purple-600"
            data-testid="view-toddler-history"
          >
            <History className="w-4 h-4 mr-1" /> View Saved ({savedReports.length})
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Form Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-purple-800 font-semibold">Select Child</Label>
            <select
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              className="w-full p-2 border border-purple-200 rounded mt-1"
              data-testid="toddler-child-select"
            >
              <option value="">Select toddler...</option>
              {displayChildren.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-purple-800 font-semibold">Date</Label>
            <Input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="border-purple-200 mt-1"
              data-testid="toddler-report-date"
            />
          </div>
          <div>
            <Label className="text-purple-800 font-semibold">Staff Name</Label>
            <Input
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              placeholder="Enter your name"
              className="border-purple-200 mt-1"
              data-testid="toddler-staff-name"
            />
          </div>
        </div>

        {/* Printable Report Preview */}
        <div className="border-2 border-purple-100 rounded-lg p-6 bg-white print:border-2 print:border-gray-400">
          {/* Header with logo and decorative elements */}
          <div className="relative mb-6">
            <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-br-full opacity-70 print:opacity-100"></div>
            <div className="absolute top-0 right-0 w-16 h-16 bg-pink-400 rounded-full -mr-4 -mt-4 opacity-70 print:opacity-100"></div>
            
            {/* Center name - centered below logo */}
            <p className="text-lg font-bold text-gray-800 text-center print:block hidden mt-2 mb-4">{centerName}</p>
            
            <div className="text-center pt-8 print:pt-2 pb-4">
              <h2 className="text-3xl font-bold text-purple-800 print:text-black">Toddler Daily Report</h2>
              <div className="flex justify-center gap-8 mt-4">
                <div className="text-left">
                  <span className="font-semibold">Name:</span>
                  <span className="ml-2 border-b border-gray-400 inline-block min-w-[150px]">
                    {getChildName() || "_________________"}
                  </span>
                </div>
                <div className="text-left">
                  <span className="font-semibold">Date:</span>
                  <span className="ml-2 border-b border-gray-400 inline-block min-w-[150px]">
                    {reportDate ? format(new Date(reportDate + 'T12:00:00'), 'MM/dd/yyyy') : "_________________"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Today I felt - Mood Section */}
          <div className="mb-4 p-4 bg-purple-50 rounded-lg print:bg-white print:border print:border-gray-300">
            <div className="flex items-center flex-wrap gap-4">
              <span className="font-bold text-purple-800 print:text-black">Today I felt:</span>
              {Object.entries(mood).map(([key, value]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={value}
                    onCheckedChange={(checked) => setMood({ ...mood, [key]: checked })}
                    className="border-purple-400 data-[state=checked]:bg-purple-500"
                  />
                  <span className="capitalize text-gray-700">{key}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Today I Learned */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg print:bg-white print:border print:border-gray-300">
            <div className="flex items-center gap-2">
              <span className="font-bold text-purple-800 print:text-black flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Today I learned:
              </span>
              <Input
                value={todayLearned}
                onChange={(e) => setTodayLearned(e.target.value)}
                placeholder="Colors, ABCs, counting..."
                className="flex-1 border-purple-200 print:border-b print:border-gray-400 print:rounded-none"
                data-testid="toddler-learned"
              />
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Diaper/Toilet Section */}
            <div className="border-2 border-purple-200 rounded-lg p-4 print:border-gray-400">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-purple-800 print:text-black">Diaper / Toilet</h3>
                <Button
                  onClick={addBathroomEntry}
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-600 print:hidden"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-200 print:border-gray-400">
                    <th className="py-2 text-left font-semibold">Time</th>
                    <th className="py-2 text-center font-semibold">D/T</th>
                    <th className="py-2 text-center font-semibold print:w-10">D</th>
                    <th className="py-2 text-center font-semibold print:w-10">W</th>
                    <th className="py-2 text-center font-semibold print:w-10">BM</th>
                    <th className="py-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  {bathroomEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-purple-100 print:border-gray-300">
                      <td className="py-2 print:py-3">
                        <Input
                          type="time"
                          value={entry.time}
                          onChange={(e) => updateBathroomEntry(entry.id, 'time', e.target.value)}
                          className="w-28 h-8 text-xs border-purple-200 print:border-0 print:border-b print:border-gray-400 print:rounded-none print:bg-transparent"
                        />
                      </td>
                      <td className="py-2 text-center print:py-3">
                        <Input
                          type="text"
                          value={entry.dtType || ""}
                          onChange={(e) => updateBathroomEntry(entry.id, 'dtType', e.target.value)}
                          className="w-10 h-8 text-xs text-center border-purple-200 print:border-0 print:border-b print:border-gray-400 print:rounded-none print:bg-transparent"
                          maxLength={1}
                        />
                      </td>
                      <td className="py-2 text-center print:py-3">
                        <div 
                          className="flex justify-center cursor-pointer"
                          onClick={() => updateBathroomEntry(entry.id, 'dry', !entry.dry)}
                        >
                          <div className={`w-5 h-5 border-2 rounded print:border-gray-400 flex items-center justify-center ${entry.dry ? 'border-purple-500 bg-purple-100' : 'border-purple-300'}`}>
                            {entry.dry && <span className="text-purple-600 font-bold text-xs">✓</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 text-center print:py-3">
                        <div 
                          className="flex justify-center cursor-pointer"
                          onClick={() => updateBathroomEntry(entry.id, 'wet', !entry.wet)}
                        >
                          <div className={`w-5 h-5 border-2 rounded print:border-gray-400 flex items-center justify-center ${entry.wet ? 'border-purple-500 bg-purple-100' : 'border-purple-300'}`}>
                            {entry.wet && <span className="text-purple-600 font-bold text-xs">✓</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 text-center print:py-3">
                        <div 
                          className="flex justify-center cursor-pointer"
                          onClick={() => updateBathroomEntry(entry.id, 'bm', !entry.bm)}
                        >
                          <div className={`w-5 h-5 border-2 rounded print:border-gray-400 flex items-center justify-center ${entry.bm ? 'border-purple-500 bg-purple-100' : 'border-purple-300'}`}>
                            {entry.bm && <span className="text-purple-600 font-bold text-xs">✓</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 print:hidden">
                        <Button
                          onClick={() => removeBathroomEntry(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-600 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2 print:text-gray-600">D = Diaper, T = Toilet, W = Wet, BM = Bowel Movement</p>
            </div>

            {/* Meals Section */}
            <div className="border-2 border-purple-200 rounded-lg p-4 print:border-gray-400">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-purple-800 print:text-black flex items-center gap-2">
                  <Utensils className="w-5 h-5" />
                  Meals
                </h3>
                <Button
                  onClick={addMeal}
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-600 print:hidden"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-200 print:border-gray-400">
                    <th className="py-2 text-left font-semibold">Time</th>
                    <th className="py-2 text-left font-semibold">Type</th>
                    <th className="py-2 text-left font-semibold">Amt</th>
                    <th className="py-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  {meals.map((meal) => (
                    <tr key={meal.id} className="border-b border-purple-100 print:border-gray-300">
                      <td className="py-2 print:py-3">
                        <Input
                          type="time"
                          value={meal.time}
                          onChange={(e) => updateMeal(meal.id, 'time', e.target.value)}
                          className="w-28 h-8 text-xs border-purple-200 print:border-0 print:border-b print:border-gray-400 print:rounded-none print:bg-transparent"
                        />
                      </td>
                      <td className="py-2 print:py-3">
                        <Input
                          value={meal.type}
                          onChange={(e) => updateMeal(meal.id, 'type', e.target.value)}
                          className="w-24 h-8 text-xs border-purple-200 print:border-0 print:border-b print:border-gray-400 print:rounded-none print:bg-transparent print:placeholder-transparent"
                        />
                      </td>
                      <td className="py-2 print:py-3">
                        <Input
                          value={meal.amount}
                          onChange={(e) => updateMeal(meal.id, 'amount', e.target.value)}
                          className="w-16 h-8 text-xs border-purple-200 print:border-0 print:border-b print:border-gray-400 print:rounded-none print:bg-transparent print:placeholder-transparent"
                        />
                      </td>
                      <td className="py-2 print:hidden">
                        <Button
                          onClick={() => removeMeal(meal.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-600 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2 print:text-gray-600">Amount: All / Some / None</p>
            </div>
          </div>

          {/* Sleep and Notes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Sleep Section */}
            <div className="border-2 border-purple-200 rounded-lg p-4 print:border-gray-400">
              <h3 className="font-bold text-purple-800 print:text-black flex items-center gap-2 mb-3">
                <Moon className="w-5 h-5" />
                Sleep
              </h3>
              <div className="space-y-3">
                {sleepPeriods.map((period) => (
                  <div key={period.id} className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium w-10">Start</span>
                      <Input
                        type="time"
                        value={period.start}
                        onChange={(e) => updateSleepPeriod(period.id, 'start', e.target.value)}
                        className="w-28 h-8 text-sm border-purple-200"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium w-10">End</span>
                      <Input
                        type="time"
                        value={period.end}
                        onChange={(e) => updateSleepPeriod(period.id, 'end', e.target.value)}
                        className="w-28 h-8 text-sm border-purple-200"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes Section */}
            <div className="border-2 border-purple-200 rounded-lg p-4 print:border-gray-400">
              <h3 className="font-bold text-purple-800 print:text-black mb-2">Notes</h3>
              <Textarea
                value={activities}
                onChange={(e) => setActivities(e.target.value)}
                placeholder="Activities, milestones, special moments..."
                className="border-purple-200 min-h-[100px] print:border-gray-400"
                data-testid="toddler-notes"
              />
            </div>
          </div>

          {/* Please Bring More Section */}
          <div className="border-2 border-purple-200 rounded-lg p-4 mb-6 print:border-gray-400">
            <h3 className="font-bold text-purple-800 print:text-black text-center mb-3">Please Bring More:</h3>
            <div className="flex flex-wrap justify-center gap-8">
              {Object.entries(bringMore).map(([key, value]) => (
                <label key={key} className="flex flex-col items-center gap-1 cursor-pointer">
                  <Checkbox
                    checked={value}
                    onCheckedChange={(checked) => setBringMore({ ...bringMore, [key]: checked })}
                    className="border-purple-400 data-[state=checked]:bg-purple-500"
                  />
                  <span className="text-xs capitalize">{key}</span>
                </label>
              ))}
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-gray-500">Other:</span>
                <Input
                  placeholder="..."
                  className="w-24 h-6 text-xs border-purple-200"
                />
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-2 border-purple-200 rounded-lg p-4 print:border-gray-400">
            <h3 className="font-bold text-purple-800 print:text-black mb-2">Comments:</h3>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Additional comments for parents..."
              className="border-purple-200 min-h-[60px] print:border-gray-400"
              data-testid="toddler-comments"
            />
          </div>

          {/* Footer with branding */}
          <div className="text-center mt-6 pt-4 border-t border-purple-200 print:border-gray-400">
            <p className="text-purple-600 font-semibold print:text-gray-600">{centerName}</p>
            <p className="text-xs text-gray-500">Staff: {staffName || "_________________"}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between print:hidden">
          <Button onClick={clearForm} variant="outline" className="border-purple-300 text-purple-600">
            Clear Form
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="save-toddler-report"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Report"}
            </Button>
            <Button 
              onClick={() => exportDailyReportPDF({
                reportType: 'toddler',
                childId: selectedChild,
                childName: children.find(c => c.id === selectedChild)?.name || 'Child',
                date: reportDate,
                staffName,
                parentEmail: getChildEmail(),
                feedings: meals.map(m => ({
                  time: m.time,
                  amount: m.amount,
                  formulaType: m.type,
                  notes: ''
                })),
                diaperChanges: bathroomEntries.map(b => ({
                  time: b.time,
                  type: b.bm ? 'BM' : b.wet ? 'Wet' : b.dry ? 'Dry' : '',
                  notes: b.dtType === 'T' ? 'Toilet' : 'Diaper'
                })),
                sleepPeriods: sleepPeriods.map(s => ({
                  startTime: s.start,
                  endTime: s.end,
                  notes: ''
                })),
                toiletTraining: bathroomEntries.filter(b => b.dtType === 'T').map(b => ({
                  time: b.time,
                  type: b.bm ? 'BM' : b.wet ? 'Wet' : 'Dry',
                  notes: ''
                })),
                mood: Object.entries(mood).filter(([_, v]) => v).map(([k]) => k).join(', '),
                activities,
                milestones: todayLearned,
                suppliesNeeded: Object.entries(bringMore).filter(([_, v]) => v).map(([k]) => k).join(', '),
                notes: comments
              }, centerName)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="export-toddler-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handlePrint} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ToddlerDailyReportTab;
